/**
 * EF-CREW-ROLES-QUALIFICATIONS-HARDENING-V1: Staff Suitability Tabs
 * 
 * Tabs for managing staff crew suitability attributes:
 * - Roles (operational roles like stagehand, driver, rigger)
 * - Qualifications (legal qualifications with expiry dates)
 * - Languages
 * - Experience Tags
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { 
  Briefcase, Award, Languages, Tag, Plus, X, Check, Calendar,
  AlertCircle, RefreshCw, Trash2
} from 'lucide-react';

// Common languages for quick selection
const COMMON_LANGUAGES = [
  'English', 'French', 'Spanish', 'German', 'Italian', 
  'Portuguese', 'Dutch', 'Arabic', 'Mandarin', 'Russian'
];

// Common experience tags
const COMMON_EXPERIENCE_TAGS = [
  'festival', 'corporate_events', 'yacht_events', 'concerts',
  'theatre', 'exhibitions', 'weddings', 'sports_events',
  'tv_production', 'film_production'
];

export function StaffSuitabilityTabs({ staffId, staffData, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [crewRoles, setCrewRoles] = useState([]);
  const [crewQualifications, setCrewQualifications] = useState([]);
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [showAddQualDialog, setShowAddQualDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newQualName, setNewQualName] = useState('');
  const [newQualExpiry, setNewQualExpiry] = useState(false);

  // Staff attributes
  const [selectedRoles, setSelectedRoles] = useState(staffData?.roles || []);
  const [qualifications, setQualifications] = useState(staffData?.qualifications || []);
  const [languages, setLanguages] = useState(staffData?.languages || []);
  const [experienceTags, setExperienceTags] = useState(staffData?.experience_tags || []);
  const [newLanguage, setNewLanguage] = useState('');
  const [newTag, setNewTag] = useState('');

  // Load crew roles and qualifications
  const loadMasterData = useCallback(async () => {
    try {
      const [rolesRes, qualsRes] = await Promise.all([
        api.get('/crew-roles'),
        api.get('/crew-qualifications'),
      ]);
      setCrewRoles(rolesRes.data || []);
      setCrewQualifications(qualsRes.data || []);
    } catch (error) {
      console.error('Failed to load crew roles/qualifications:', error);
    }
  }, []);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Update from staffData when it changes
  useEffect(() => {
    setSelectedRoles(staffData?.roles || []);
    setQualifications(staffData?.qualifications || []);
    setLanguages(staffData?.languages || []);
    setExperienceTags(staffData?.experience_tags || []);
  }, [staffData]);

  // Save roles
  const saveRoles = async () => {
    setLoading(true);
    try {
      await api.patch(`/staff/${staffId}/roles`, { roles: selectedRoles });
      toast.success('Roles updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update roles');
    } finally {
      setLoading(false);
    }
  };

  // Save qualifications
  const saveQualifications = async () => {
    setLoading(true);
    try {
      await api.patch(`/staff/${staffId}/qualifications`, { qualifications });
      toast.success('Qualifications updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update qualifications');
    } finally {
      setLoading(false);
    }
  };

  // Save languages
  const saveLanguages = async () => {
    setLoading(true);
    try {
      await api.patch(`/staff/${staffId}/languages`, { languages });
      toast.success('Languages updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update languages');
    } finally {
      setLoading(false);
    }
  };

  // Save experience tags
  const saveExperienceTags = async () => {
    setLoading(true);
    try {
      await api.patch(`/staff/${staffId}/experience-tags`, { experience_tags: experienceTags });
      toast.success('Experience tags updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update experience tags');
    } finally {
      setLoading(false);
    }
  };

  // Create new crew role
  const createCrewRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      await api.post('/crew-roles', { name: newRoleName, display_name: newRoleName });
      toast.success(`Role "${newRoleName}" created`);
      setNewRoleName('');
      setShowAddRoleDialog(false);
      loadMasterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create role');
    }
  };

  // Create new qualification
  const createQualification = async () => {
    if (!newQualName.trim()) return;
    try {
      await api.post('/crew-qualifications', { 
        name: newQualName, 
        display_name: newQualName,
        expiry_required: newQualExpiry 
      });
      toast.success(`Qualification "${newQualName}" created`);
      setNewQualName('');
      setNewQualExpiry(false);
      setShowAddQualDialog(false);
      loadMasterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create qualification');
    }
  };

  // Toggle role selection
  const toggleRole = (roleName) => {
    setSelectedRoles(prev => 
      prev.includes(roleName) 
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  // Add qualification to staff
  const addQualification = (qualId) => {
    if (qualifications.some(q => q.qualification_id === qualId)) return;
    setQualifications(prev => [...prev, { 
      qualification_id: qualId, 
      expiry_date: null,
      verified: false 
    }]);
  };

  // Remove qualification from staff
  const removeQualification = (qualId) => {
    setQualifications(prev => prev.filter(q => q.qualification_id !== qualId));
  };

  // Update qualification expiry
  const updateQualificationExpiry = (qualId, expiryDate) => {
    setQualifications(prev => prev.map(q => 
      q.qualification_id === qualId ? { ...q, expiry_date: expiryDate } : q
    ));
  };

  // Add language
  const addLanguage = (lang) => {
    if (!lang.trim() || languages.includes(lang.trim())) return;
    setLanguages(prev => [...prev, lang.trim()]);
    setNewLanguage('');
  };

  // Add experience tag
  const addExperienceTag = (tag) => {
    const normalizedTag = tag.trim().toLowerCase().replace(/\s+/g, '_');
    if (!normalizedTag || experienceTags.includes(normalizedTag)) return;
    setExperienceTags(prev => [...prev, normalizedTag]);
    setNewTag('');
  };

  return (
    <div className="space-y-4" data-testid="staff-suitability-tabs">
      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="roles" className="flex items-center gap-1">
            <Briefcase className="h-3 w-3" />
            Roles ({selectedRoles.length})
          </TabsTrigger>
          <TabsTrigger value="qualifications" className="flex items-center gap-1">
            <Award className="h-3 w-3" />
            Qualifications ({qualifications.length})
          </TabsTrigger>
          <TabsTrigger value="languages" className="flex items-center gap-1">
            <Languages className="h-3 w-3" />
            Languages ({languages.length})
          </TabsTrigger>
          <TabsTrigger value="experience" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            Experience ({experienceTags.length})
          </TabsTrigger>
        </TabsList>

        {/* ROLES TAB */}
        <TabsContent value="roles" className="mt-4">
          <Card>
            <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Operational Roles</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAddRoleDialog(true)}>
                <Plus className="h-3 w-3 mr-1" />
                New Role
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {crewRoles.map((role) => (
                  <Badge
                    key={role.id || role.name}
                    variant={selectedRoles.includes(role.name) ? "default" : "outline"}
                    className="cursor-pointer py-1 px-3"
                    onClick={() => toggleRole(role.name)}
                    data-testid={`role-badge-${role.name}`}
                  >
                    {selectedRoles.includes(role.name) && <Check className="h-3 w-3 mr-1" />}
                    {role.display_name || role.name}
                  </Badge>
                ))}
                {crewRoles.length === 0 && (
                  <p className="text-sm text-gray-500">No roles defined. Create one to get started.</p>
                )}
              </div>
              <Button size="sm" onClick={saveRoles} disabled={loading} data-testid="save-roles-btn">
                {loading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                Save Roles
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* QUALIFICATIONS TAB */}
        <TabsContent value="qualifications" className="mt-4">
          <Card>
            <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Legal Qualifications</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAddQualDialog(true)}>
                <Plus className="h-3 w-3 mr-1" />
                New Qualification
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {/* Available qualifications to add */}
              <div className="mb-4">
                <Label className="text-xs text-gray-500">Add qualification:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {crewQualifications
                    .filter(q => !qualifications.some(sq => sq.qualification_id === q.name))
                    .map((qual) => (
                      <Badge
                        key={qual.id || qual.name}
                        variant="outline"
                        className="cursor-pointer"
                        onClick={() => addQualification(qual.name)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {qual.display_name || qual.name}
                        {qual.expiry_required && <Calendar className="h-3 w-3 ml-1 text-amber-500" />}
                      </Badge>
                    ))}
                </div>
              </div>

              {/* Crew member's qualifications */}
              <div className="space-y-2">
                {qualifications.map((qual) => {
                  const masterQual = crewQualifications.find(q => q.name === qual.qualification_id);
                  const isExpired = qual.expiry_date && new Date(qual.expiry_date) < new Date();
                  
                  return (
                    <div 
                      key={qual.qualification_id}
                      className={`flex items-center justify-between p-2 rounded border ${isExpired ? 'border-red-500/50 bg-red-500/10' : 'border-white/10'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Award className={`h-4 w-4 ${isExpired ? 'text-red-400' : 'text-green-400'}`} />
                        <span className="text-sm">{masterQual?.display_name || qual.qualification_id}</span>
                        {isExpired && (
                          <Badge variant="destructive" className="text-xs">EXPIRED</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {(masterQual?.expiry_required || qual.expiry_date) && (
                          <Input
                            type="date"
                            value={qual.expiry_date || ''}
                            onChange={(e) => updateQualificationExpiry(qual.qualification_id, e.target.value)}
                            className="w-40 text-xs"
                            data-testid={`qual-expiry-${qual.qualification_id}`}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQualification(qual.qualification_id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {qualifications.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No qualifications assigned</p>
                )}
              </div>

              <Button size="sm" onClick={saveQualifications} disabled={loading} className="mt-4" data-testid="save-quals-btn">
                {loading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                Save Qualifications
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LANGUAGES TAB */}
        <TabsContent value="languages" className="mt-4">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Languages Spoken</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {/* Quick add common languages */}
              <div className="mb-4">
                <Label className="text-xs text-gray-500">Quick add:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COMMON_LANGUAGES.filter(l => !languages.includes(l)).map((lang) => (
                    <Badge
                      key={lang}
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => addLanguage(lang)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {lang}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Custom language input */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  placeholder="Add custom language..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && addLanguage(newLanguage)}
                />
                <Button size="sm" variant="outline" onClick={() => addLanguage(newLanguage)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Selected languages */}
              <div className="flex flex-wrap gap-2 mb-4">
                {languages.map((lang) => (
                  <Badge key={lang} className="py-1 px-3">
                    {lang}
                    <X
                      className="h-3 w-3 ml-2 cursor-pointer"
                      onClick={() => setLanguages(prev => prev.filter(l => l !== lang))}
                    />
                  </Badge>
                ))}
                {languages.length === 0 && (
                  <p className="text-sm text-gray-500">No languages added</p>
                )}
              </div>

              <Button size="sm" onClick={saveLanguages} disabled={loading} data-testid="save-languages-btn">
                {loading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                Save Languages
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPERIENCE TAB */}
        <TabsContent value="experience" className="mt-4">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Experience Tags</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {/* Quick add common tags */}
              <div className="mb-4">
                <Label className="text-xs text-gray-500">Quick add:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COMMON_EXPERIENCE_TAGS.filter(t => !experienceTags.includes(t)).map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => addExperienceTag(tag)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {tag.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Custom tag input */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add custom experience tag..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && addExperienceTag(newTag)}
                />
                <Button size="sm" variant="outline" onClick={() => addExperienceTag(newTag)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Selected tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {experienceTags.map((tag) => (
                  <Badge key={tag} className="py-1 px-3">
                    {tag.replace(/_/g, ' ')}
                    <X
                      className="h-3 w-3 ml-2 cursor-pointer"
                      onClick={() => setExperienceTags(prev => prev.filter(t => t !== tag))}
                    />
                  </Badge>
                ))}
                {experienceTags.length === 0 && (
                  <p className="text-sm text-gray-500">No experience tags added</p>
                )}
              </div>

              <Button size="sm" onClick={saveExperienceTags} disabled={loading} data-testid="save-experience-btn">
                {loading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                Save Experience Tags
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Role Dialog */}
      <Dialog open={showAddRoleDialog} onOpenChange={setShowAddRoleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Crew Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Role Name *</Label>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g., rigger, forklift_operator"
                data-testid="new-role-input"
              />
              <p className="text-xs text-gray-500 mt-1">Will be normalized to lowercase with underscores</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoleDialog(false)}>Cancel</Button>
            <Button onClick={createCrewRole} disabled={!newRoleName.trim()} data-testid="create-role-btn">
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Qualification Dialog */}
      <Dialog open={showAddQualDialog} onOpenChange={setShowAddQualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Qualification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Qualification Name *</Label>
              <Input
                value={newQualName}
                onChange={(e) => setNewQualName(e.target.value)}
                placeholder="e.g., forklift_license, first_aid"
                data-testid="new-qual-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="expiry_required"
                checked={newQualExpiry}
                onCheckedChange={(checked) => setNewQualExpiry(checked)}
              />
              <Label htmlFor="expiry_required" className="text-sm">
                Requires expiry date (e.g., licenses that need renewal)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddQualDialog(false)}>Cancel</Button>
            <Button onClick={createQualification} disabled={!newQualName.trim()} data-testid="create-qual-btn">
              Create Qualification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StaffSuitabilityTabs;
